import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';
import { History, User, Clock, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface AuditLog {
  id: string;
  action: string;
  actor_id: string;
  actor_name: string;
  client_id: string | null;
  details: string;
  created_at: string;
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await SuperAdminService.getAuditLogs();
        setLogs(data);
      } catch {
        toast.error('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = search
    ? logs.filter(l =>
        l.action.toLowerCase().includes(search.toLowerCase()) ||
        l.actor_name.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  const formatDetails = (details: string) => {
    try {
      const obj = JSON.parse(details);
      return Object.entries(obj)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(' · ');
    } catch {
      return details || '—';
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F7F2EA] text-[#243238]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Audit Logs" />

        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <h1 className="text-sm sm:text-base font-extrabold tracking-tight text-[#243238] uppercase">Audit Logs</h1>
          <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
        </header>

        <main className="flex-grow p-4 sm:p-8">
          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by action or actor..."
              className="w-full max-w-md px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] font-semibold bg-white"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-[#1F5A43] border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#8D8A83]">
              <History className="w-12 h-12 mb-3 opacity-40 text-[#1F5A43]" />
              <p className="text-lg font-medium text-[#6F706C]">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((log, i) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="rounded-[18px] border border-[#E4D8C8] p-5 hover:shadow-earth-lg transition-all bg-[#FFFDF9]"
                >
                  <div className="flex items-start gap-4">
                    {/* Timeline dot */}
                    <div className="mt-1 w-10 h-10 rounded-xl bg-[#DFF5E7] border border-[#1F7A4F]/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#1F7A4F]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#243238] text-sm">{log.action}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-[#6F706C]">
                        <span className="flex items-center gap-1"><User className="w-3 h-3 text-[#1F5A43]" /> {log.actor_name}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#8A4B22]" /> {new Date(log.created_at).toLocaleString()}</span>
                      </div>
                      {log.details && log.details !== '{}' && (
                        <p className="mt-2 text-xs text-[#6F706C] bg-[#F7F2EA] border border-[#DDD0BE]/60 rounded-xl px-3 py-2 font-mono truncate">{formatDetails(log.details)}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AuditLogs;
